import {
    Label,
    Ticket,
    TicketId,
    TicketKey,
    TicketLinkTypeId,
    TicketSearchResult,
    ticketApi,
} from "@/api/tickets";
import { isDefined } from "@/utils";
import { defineQuery, defineQueryOptions, useQuery, useQueryCache } from "@pinia/colada";
import { computed } from "vue";

export const TICKET_KEYS = {
    root: ["tickets"] as const,
    detail: (id: TicketId | TicketKey) => [...TICKET_KEYS.root, "detail", id] as const,
    // Hover-preview summaries; a null result marks a key that resolved to
    // not-found/no-access so it isn't refetched on every hover.
    summary: (key: string) => [...TICKET_KEYS.root, "summary", key] as const,
};

export const ticketDetailQuery = defineQueryOptions((ticketId: TicketId | TicketKey) => ({
    key: TICKET_KEYS.detail(ticketId),
    query: () => ticketApi.get(ticketId),
    // Revisiting a ticket renders the cached copy instantly and revalidates it.
    staleTime: 0,
}));

export const ticketSummaryQuery = defineQueryOptions((key: string) => ({
    key: TICKET_KEYS.summary(key),
    query: () => ticketApi.getByKey(key),
    staleTime: 5 * 60_000,
}));

export const TICKET_LOOKUP_KEYS = {
    root: [...TICKET_KEYS.root, "lookups"] as const,
    labels: () => [...TICKET_LOOKUP_KEYS.root, "labels"] as const,
    linkTypes: () => [...TICKET_LOOKUP_KEYS.root, "linkTypes"] as const,
    priorities: () => [...TICKET_LOOKUP_KEYS.root, "priorities"] as const,
    resolutions: () => [...TICKET_LOOKUP_KEYS.root, "resolutions"] as const,
    statuses: () => [...TICKET_LOOKUP_KEYS.root, "statuses"] as const,
    types: () => [...TICKET_LOOKUP_KEYS.root, "types"] as const,
};

const LOOKUP_STALE_MS = 5 * 60_000;

function toMap<K, V>(values: V[] | undefined, key: (value: V) => K): Map<K, V> | undefined {
    return values ? new Map(values.map((v) => [key(v), v])) : undefined;
}

// The shared ticket vocabulary (statuses, priorities, ...). Consuming this
// anywhere activates the queries, so no explicit load calls are needed.
export const useTicketLookups = defineQuery(() => {
    const labelsQuery = useQuery({
        key: TICKET_LOOKUP_KEYS.labels(),
        query: () => ticketApi.getLabels(),
        staleTime: LOOKUP_STALE_MS,
    });
    const linkTypesQuery = useQuery({
        key: TICKET_LOOKUP_KEYS.linkTypes(),
        query: () => ticketApi.getLinkTypes(),
        staleTime: LOOKUP_STALE_MS,
    });
    const prioritiesQuery = useQuery({
        key: TICKET_LOOKUP_KEYS.priorities(),
        query: () => ticketApi.getPriorities(),
        staleTime: LOOKUP_STALE_MS,
    });
    const resolutionsQuery = useQuery({
        key: TICKET_LOOKUP_KEYS.resolutions(),
        query: () => ticketApi.getResolutions(),
        staleTime: LOOKUP_STALE_MS,
    });
    const statusesQuery = useQuery({
        key: TICKET_LOOKUP_KEYS.statuses(),
        query: () => ticketApi.getStatuses(),
        staleTime: LOOKUP_STALE_MS,
    });
    const typesQuery = useQuery({
        key: TICKET_LOOKUP_KEYS.types(),
        query: () => ticketApi.getTypes(),
        staleTime: LOOKUP_STALE_MS,
    });

    const labels = computed(() => toMap(labelsQuery.data.value, (l) => l.id));
    const linkTypes = computed(() => toMap(linkTypesQuery.data.value, (lt) => lt.id));
    const priorities = computed(() => toMap(prioritiesQuery.data.value, (p) => p.id));
    const resolutions = computed(() => toMap(resolutionsQuery.data.value, (r) => r.id));
    const statuses = computed(() => toMap(statusesQuery.data.value, (s) => s.id));
    const types = computed(() => toMap(typesQuery.data.value, (t) => t.id));

    return {
        labels,
        linkTypes,
        priorities,
        resolutions,
        statuses,
        types,
        getLabels: (ticket: Ticket) =>
            ticket.labels.map((l) => labels.value?.get(l)).filter(isDefined),
        getLinkType: (link: TicketLinkTypeId) => linkTypes.value?.get(link),
        getPriority: (ticket: Ticket | TicketSearchResult) =>
            priorities.value?.get(ticket.priorityId),
        getResolution: (ticket: Ticket) =>
            ticket.resolutionId ? resolutions.value?.get(ticket.resolutionId) : undefined,
        getStatus: (ticket: Ticket | TicketSearchResult) =>
            statuses.value?.get(ticket.ticketStatusId),
        getType: (ticket: Ticket | TicketSearchResult) => types.value?.get(ticket.ticketTypeId),
        getDueDate: (ticket: Ticket) => (ticket.dueDate ? new Date(ticket.dueDate) : null),
    };
});

export async function setTicketLabels(
    ticket: Ticket,
    updateLabels: Label[],
    newLabels: Label[]
): Promise<void> {
    const ids = [...updateLabels, ...newLabels].map((l) => l.id);
    await ticketApi.setLabels(ticket.id, ids);
    if (newLabels.length > 0) {
        await useQueryCache().invalidateQueries({ key: TICKET_LOOKUP_KEYS.labels() });
    }
}
